import { useCallback } from 'react';
import { Shape, Tool } from '../types/Shape';
import { findNearestConnectionPoint, findShapeAtPoint, getRelativePosition } from '../utils/connectionUtils';
import { useHistory } from './useHistory';

export interface UseCanvasMouseDownProps {
  // Current state
  currentTool: Tool;
  shapes: Shape[];
  stageScale: number;
  isConnecting: boolean;
  editingShape: string | null;

  // Defaults
  defaultStartArrow: boolean;
  defaultEndArrow: boolean;
  defaultIsCurved: boolean;
  defaultArrowStroke: string;
  defaultArrowStrokeWidth: number;
  defaultFill: string;
  defaultStroke: string;
  defaultStrokeWidth: number;
  defaultFontSize: number;

  // Setters
  setIsCanvasDragging: React.Dispatch<React.SetStateAction<boolean>>;
  setIsWheelPanning: React.Dispatch<React.SetStateAction<boolean>>;
  setCanvasDragStart: React.Dispatch<React.SetStateAction<{ x: number; y: number } | null>>;
  setShowResizeHandles: React.Dispatch<React.SetStateAction<boolean>>;
  setSelectedShapes: React.Dispatch<React.SetStateAction<string[]>>;
  setIsLassoSelecting: React.Dispatch<React.SetStateAction<boolean>>;
  setLassoStart: React.Dispatch<React.SetStateAction<{ x: number; y: number } | null>>;
  setLassoEnd: React.Dispatch<React.SetStateAction<{ x: number; y: number } | null>>;
  setIsConnecting: React.Dispatch<React.SetStateAction<boolean>>;
  setShowConnectionPoints: React.Dispatch<React.SetStateAction<boolean>>;
  setCurrentShape: React.Dispatch<React.SetStateAction<Partial<Shape> | null>>;
  setIsDrawing: React.Dispatch<React.SetStateAction<boolean>>;

  // Handlers
  handleTextEditComplete: () => void;

  // History
  history: ReturnType<typeof useHistory>;
  hasActionStarted: React.MutableRefObject<boolean>;
}

export interface UseCanvasMouseDownReturn {
  handleStageMouseDown: (e: any) => void;
}

export function useCanvasMouseDown({
  currentTool,
  shapes,
  stageScale,
  isConnecting,
  editingShape,
  defaultStartArrow,
  defaultEndArrow,
  defaultIsCurved,
  defaultArrowStroke,
  defaultArrowStrokeWidth,
  defaultFill,
  defaultStroke,
  defaultStrokeWidth,
  defaultFontSize,
  setIsCanvasDragging,
  setIsWheelPanning,
  setCanvasDragStart,
  setShowResizeHandles,
  setSelectedShapes,
  setIsLassoSelecting,
  setLassoStart,
  setLassoEnd,
  setIsConnecting,
  setShowConnectionPoints,
  setCurrentShape,
  setIsDrawing,
  handleTextEditComplete,
  history,
  hasActionStarted,
}: UseCanvasMouseDownProps): UseCanvasMouseDownReturn {

  const handleStageMouseDown = useCallback((e: any) => {
    // Complete editing if in progress
    if (editingShape) {
      handleTextEditComplete();
      return;
    }

    // Middle button (wheel) - start canvas drag
    if (e.evt.button === 1) {
      e.cancelBubble = true;
      const stage = e.target.getStage();
      if (stage) {
        const pointer = stage.getPointerPosition();
        if (pointer) {
          setIsCanvasDragging(true);
          setIsWheelPanning(true);
          setCanvasDragStart({ x: pointer.x, y: pointer.y });
          if (stage.container()) {
            stage.container().style.cursor = 'grabbing';
          }
        }
      }
      return;
    }

    // Ignore non-left button clicks
    if (e.evt.button !== 0) {
      return;
    }

    const stage = e.target.getStage();
    if (!stage) return;

    const pos = stage.getPointerPosition();
    if (!pos) return;

    // Calculate actual coordinates considering stage transform
    const scale = stage.scaleX();
    const currentStagePos = { x: stage.x(), y: stage.y() };
    const actualX = (pos.x - currentStagePos.x) / scale;
    const actualY = (pos.y - currentStagePos.y) / scale;

    // Start lasso selection on empty canvas click (select tool only)
    if (e.target === e.target.getStage() && currentTool === 'select') {
      setShowResizeHandles(false);
      setSelectedShapes([]);
      setIsLassoSelecting(true);
      setLassoStart({ x: actualX, y: actualY });
      setLassoEnd({ x: actualX, y: actualY });
      return;
    }

    // Deselect when clicking on empty area
    if (e.target === e.target.getStage()) {
      setShowResizeHandles(false);
      setSelectedShapes([]);
    }

    // Arrow drawing
    if (currentTool === 'arrow') {
      if (!isConnecting) {
        const nearestPoint = findNearestConnectionPoint(actualX, actualY, shapes, 20 / stageScale);

        let startX = actualX;
        let startY = actualY;
        let startShapeId: string | undefined;
        let startConnectionPoint: 'top' | 'right' | 'bottom' | 'left' | undefined;
        let startRelativeX: number | undefined;
        let startRelativeY: number | undefined;

        if (nearestPoint) {
          startX = nearestPoint.x;
          startY = nearestPoint.y;
          startShapeId = nearestPoint.shapeId;
          startConnectionPoint = nearestPoint.pointType;
        } else {
          const shapeAtPoint = findShapeAtPoint(actualX, actualY, shapes);
          if (shapeAtPoint) {
            startShapeId = shapeAtPoint.id;
            const relativePos = getRelativePosition(actualX, actualY, shapeAtPoint);
            if (relativePos) {
              startRelativeX = relativePos.relativeX;
              startRelativeY = relativePos.relativeY;
            }
          }
        }

        const maxOrder = Math.max(...shapes.map(s => s.order ?? 0), 0);
        const newOrder = maxOrder % 2 === 0 ? maxOrder + 2 : maxOrder + 1;

        setIsConnecting(true);
        setShowConnectionPoints(true);
        setCurrentShape({
          id: 'temp-arrow',
          type: 'arrow',
          x: startX,
          y: startY,
          width: 0,
          height: 0,
          stroke: defaultArrowStroke,
          strokeWidth: defaultArrowStrokeWidth,
          isCurved: defaultIsCurved,
          startShapeId,
          startConnectionPoint,
          startRelativeX,
          startRelativeY,
          startArrow: defaultStartArrow,
          endArrow: defaultEndArrow,
          order: newOrder,
          renderMode: 'html',
        });
      }
      return;
    }

    // Shape drawing (rect, circle, code, md)
    if (currentTool === 'rect' || currentTool === 'circle' || currentTool === 'code' || currentTool === 'md') {
      const maxOrder = Math.max(...shapes.map(s => s.order ?? 0), 0);
      const newOrder = maxOrder % 2 === 0 ? maxOrder + 2 : maxOrder + 1;

      const newShape: Partial<Shape> = {
        id: Date.now().toString(),
        type: currentTool,
        x: actualX,
        y: actualY,
        width: 0,
        height: 0,
        fill: defaultFill,
        stroke: defaultStroke,
        strokeWidth: defaultStrokeWidth,
        fontSize: defaultFontSize,
        language: currentTool === 'code' ? 'javascript' : undefined,
        code: currentTool === 'code' ? '// 코드를 입력하세요' : undefined,
        markdown: currentTool === 'md' ? '# Markdown' : undefined,
        order: newOrder,
        renderMode: 'html',
      };

      if (!hasActionStarted.current) {
        history.pushHistory(shapes);
        hasActionStarted.current = true;
      }

      setCurrentShape(newShape);
      setIsDrawing(true);
    }
  }, [
    editingShape,
    handleTextEditComplete,
    currentTool,
    isConnecting,
    shapes,
    stageScale,
    defaultStartArrow,
    defaultEndArrow,
    defaultIsCurved,
    defaultArrowStroke,
    defaultArrowStrokeWidth,
    defaultFill,
    defaultStroke,
    defaultStrokeWidth,
    defaultFontSize,
    history,
    hasActionStarted,
    setIsCanvasDragging,
    setIsWheelPanning,
    setCanvasDragStart,
    setShowResizeHandles,
    setSelectedShapes,
    setIsLassoSelecting,
    setLassoStart,
    setLassoEnd,
    setIsConnecting,
    setShowConnectionPoints,
    setCurrentShape,
    setIsDrawing,
  ]);

  return { handleStageMouseDown };
}
